import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import UpdateCompanyInfo from "./UpdateCompanyInfo";
import {
  gxCompanyUpdateApi,
  gxCompanyviewApi,
  gxSmeFileUploadApi,
  gxSmeUsersCompanyProfileUpdateApi,
} from "@/app/apiConfig/services/customerAbstractApiLayer";
import { ICON_BASE_PATH } from "@/app/utils/config";
import "../UserProfile.css";
import type {
  CompanyUpdatepayload,
  CompanyView,
  SmeUsersCompanyProfileUpdatePayload,
  UserView,
} from "@/app/types/userprofile";
import { CompanyInfoLoading } from "@/app/components/webloading/shimmer/MyprofileLoading";
import AppDrawer from "@/app/models/drawer/AppDrawer";
import { t } from "i18next";
import ErrorModal from "../../payments/initiateANewTransfer/ErrorModal";
import { useSelector } from "react-redux";
import type { RootState } from "@/app/redux/store";
import ProfileStatements from "./ProfileStatements";
import Mobilefiled from "@/app/components/common/actions/inputs-b/Mobilefiled";
import { Switch, type SwitchChangeEvent } from "@progress/kendo-react-inputs";
import {
  formatRenewalDate,
  TRADE_LICENSE_DOC_TYPE,
  type SmeFileUploadApiResponse,
} from "./UpdateCompanyInfo";
import NoDataFound from "@/app/components/common/warning/NoDataFound";

// ............................... getSchemaBySection  ................................
const getSchemaBySection = (updateSection: string | null) => {
  switch (updateSection) {
    case "address":
      return yup.object().shape({
        addressLine1: yup.string().required("Address Line 1 is required"),
        addressLine2: yup.string().nullable(),
        addressLine3: yup.string().nullable(),
        addressLine4: yup.string().nullable(),
        countryCode2: yup.string().required("Country Code is required"),
      });
    case "landline":
      return yup.object().shape({
        companyLandline: yup.string().required("Company Landline is required"),
      });
    case "poboxInfo":
      return yup.object().shape({
        companyPoBox: yup.string().required("PO Box is required"),
      });
    case "renewnow":
      return yup.object().shape({
        tradeLicenseExpiryDate: yup.string().required("End Date is required"),
      });
    default:
      return yup.object().shape({});
  }
};

// ............................... formatDate  ................................
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

// ............................... buildCompanyUpdatePayload ................................
const buildCompanyUpdatePayload = (
  company: CompanyView,
  overrides: Record<string, any> = {},
): CompanyUpdatepayload => ({
  ...company,
  smeLogoName: company.smeLogoName ?? undefined,
  smeLogoPath: company.smeLogoPath ?? undefined,
  updtDt: new Date().toISOString(),
  thirdPartyLink: "N",
  companyFavourite: "N",
  ...overrides,
});

const SME_USERS_PROFILE_UPDATE_SECTIONS = ["address", "landline", "poboxInfo"];

// ............................... buildSmeUsersCompanyProfileUpdatePayload ................................
const buildSmeUsersCompanyProfileUpdatePayload = (
  company: CompanyView,
  data: any,
): SmeUsersCompanyProfileUpdatePayload => ({
  groupSmeCompId: (company as any).groupSmeCompId ?? company.groupCompId,
  addressLine1: data.addressLine1 ?? company.addressLine1,
  addressLine2: data.addressLine2 ?? company.addressLine2,
  addressLine3: data.addressLine3 ?? company.addressLine3,
  addressLine4: data.addressLine4 ?? company.addressLine4,
  companyLandline: data.companyLandline ?? company.companyLandline,
  companyPoBox: data.companyPoBox ?? company.companyPoBox,
});

interface PersonalInfo {
  personalInfo: UserView | null;
}

const CompanyInfo = ({ personalInfo }: PersonalInfo) => {
  const { handleSubmit, setValue, watch } = useForm({
    resolver: yupResolver(getSchemaBySection("address")),
  });

  const [company, setCompany] = useState<CompanyView | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [updateSection, setUpdateSection] = useState<
    "address" | "landline" | "poboxInfo" | "renewnow" | null
  >(null);
  const [isErrorOpen, setErrorOpen] = useState(false);
  const userData = useSelector((state: RootState) => state.userData.data);
  const loginName = userData?.userType;

  const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [whatsappDialCode, setWhatsappDialCode] = useState("");
  const [whatsappNumberValid, setWhatsappNumberValid] = useState(true);
  const [whatsappTriggerValidation, setWhatsappTriggerValidation] =
    useState(false);

  // ............................... calculateExpirationStatus ................................
  const calculateExpirationStatus = (expiryDate: string) => {
    if (!expiryDate) return null;

    const currentDate = new Date();
    const expiryDateObj = new Date(expiryDate);
    const timeDiff = expiryDateObj.getTime() - currentDate.getTime();
    const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (daysRemaining <= 30 && daysRemaining >= 0) {
      return `${daysRemaining} days left - Expiring Soon`;
    }

    return null;
  };

  // ............................... expirationMessage  ................................
  const expirationMessage = calculateExpirationStatus(
    company?.tradeLicenseExpiryDate || "",
  );

  const documentTypes = useMemo(
    () =>
      Array.from(
        new Set(
          (company?.documentsDetails ?? [])
            .map((doc) => doc?.docType)
            .filter(Boolean),
        ),
      ) as string[],
    [company],
  );

  // ............................... handleDrawerClose  ................................
  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setUpdateSection(null);
  };

  // ............................... getCompanyApi  ................................
  const getCompanyApi = async () => {
    if (!personalInfo?.portalCompanyId) return;
    setIsLoading(true);
    try {
      const requestPayload = { type: personalInfo?.portalCompanyId };
      const response = await gxCompanyviewApi(requestPayload);
      if (response?.status === 1 && response?.data) {
        setCompany(response.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // ............................... handleUpdateClick  ................................
  const handleUpdateClick = (
    section: "address" | "landline" | "poboxInfo" | "renewnow",
  ) => {
    setUpdateSection(section);
    setIsDrawerOpen(true);
  };

  const cleanData = (data: any) => ({
    ...data,
    emailId: data.emailId?.replace("^", ""),
    tradeLicenseExpiryDate: data.tradeLicenseExpiryDate
      ? new Date(data.tradeLicenseExpiryDate).toISOString()
      : undefined,
    tradeLicenseNewExpiryDate: data.tradeLicenseNewExpiryDate
      ? new Date(data.tradeLicenseNewExpiryDate).toISOString()
      : undefined,
  });

  // ............................... handleRenewLicenseSubmit ................................
  const handleRenewLicenseSubmit = async (data: any) => {
    if (!company?.vcifId) return;

    const formData = new FormData();
    formData.append("type", "update");
    formData.append("vcifId", company.vcifId);
    formData.append("documentType", TRADE_LICENSE_DOC_TYPE);
    if (data.tradeLicenseExpiryDate) {
      formData.append(
        "renewalDate",
        formatRenewalDate(data.tradeLicenseExpiryDate),
      );
    }
    if (data.tradeLicenseFile) {
      formData.append("file", data.tradeLicenseFile);
    }

    try {
      const response: SmeFileUploadApiResponse = await gxSmeFileUploadApi(
        formData as any,
      );
      if (response?.status === 1) {
        setIsDrawerOpen(false);
        getCompanyApi();
      } else {
        setErrorOpen(true);
      }
    } catch (error) {
      console.error(error);
      setErrorOpen(true);
    }
  };

  // ............................... async  ................................
  const onSubmit = async (data: any) => {
    console.log(company, "company");

    if (company === null) return;

    if (updateSection === "renewnow") {
      console.log(data, "data");

      await handleRenewLicenseSubmit(data);
      return;
    }

    if (
      updateSection &&
      SME_USERS_PROFILE_UPDATE_SECTIONS.includes(updateSection)
    ) {
      const requestPayload = buildSmeUsersCompanyProfileUpdatePayload(
        company,
        data,
      );

      try {
        const response =
          await gxSmeUsersCompanyProfileUpdateApi(requestPayload);
        if (response?.status === 0) {
          setErrorOpen(true);
          return;
        }
        if (response?.status === 1) {
          setIsDrawerOpen(false);
          getCompanyApi();
        }
      } catch (error) {
        console.error(error);
      }
      return;
    }

    const requestPayload = buildCompanyUpdatePayload(company, cleanData(data));

    try {
      const response = await gxCompanyUpdateApi(requestPayload);
      if (response?.status === 0) {
        setErrorOpen(true);
        return;
      }
      if (response?.status === 1) {
        setIsDrawerOpen(false);
        getCompanyApi();
      }
    } catch (error) {
      console.error(error);
    }
  };

  // ............................... getTitleAndSubtitle  ................................
  const getTitleAndSubtitle = (updateSection: string | null) => {
    switch (updateSection) {
      case "address":
        return { title: "Company", subtitle: "Address" };
      case "landline":
        return { title: "Company", subtitle: "Landline" };
      case "poboxInfo":
        return { title: "Company", subtitle: "PO Box Info" };
      case "renewnow":
        return { title: "Trade", subtitle: " License" };
      default:
        return { title: "Update", subtitle: "" };
    }
  };

  const { title, subtitle } = getTitleAndSubtitle(updateSection);

  // ............................... useEffect  ................................
  useEffect(() => {
    getCompanyApi();
  }, []);

  // ............................... useEffect: sync WhatsApp prefs from company ................................
  useEffect(() => {
    if (!company) return;
    const c = company as any;
    setWhatsappOptIn(c.whatsappOptIn === "Y" || c.whatsappOptIn === true);
    setWhatsappNumber(c.whatsappNumber ?? "");
    setWhatsappDialCode(c.whatsappDialCode ?? "");
  }, [company]);

  // ............................... handleToggleWhatsapp ................................
  const handleToggleWhatsapp = (e: SwitchChangeEvent) => {
    setWhatsappOptIn(e.value);
    setWhatsappTriggerValidation(false);
    setWhatsappNumberValid(true);
  };

  // ............................... handleSaveWhatsappPreference ................................

  return (
    <>
      {isLoading ? (
        <CompanyInfoLoading />
      ) : (
        <>
          {company ? (
            <>
              <div className="gx-profile-tab-container">
                <div className="gx-profile-title">
                  <h3 className="gx-h3-b">{t("company_info")}</h3>
                </div>

                <div className="information-list">
                  <div className="information-list-child">
                    <div className="information-list-img">
                      <img
                        src={`${ICON_BASE_PATH}userprofile.svg`}
                        alt="Company"
                      />
                    </div>
                    <p className="gx-p12-sb">
                      {t("company_name")}
                      <span className="gx-p14-sb">
                        {company?.groupCompName || "N/A"}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="information-list">
                  <div className="information-list-child">
                    <div className="information-list-img">
                      <img
                        src={`${ICON_BASE_PATH}onboarded.svg`}
                        alt="Onboarded"
                      />
                    </div>
                    <p className="gx-p12-sb">
                      {t("contact_name")}{" "}
                      <span className="gx-p14-sb">
                        {company?.contactName || "N/A"}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="information-list">
                  <div className="information-list-child">
                    <div className="information-list-img">
                      <img
                        src={`${ICON_BASE_PATH}userprofile.svg`}
                        alt="Email"
                      />
                    </div>
                    <p className="gx-p12-sb">
                      {t("email_address")}
                      <span className="gx-p14-sb">
                        {company?.maskEmailId || company?.emailId || "N/A"}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="information-list">
                  <div className="information-list-child">
                    <div className="information-list-img">
                      <img
                        src={`${ICON_BASE_PATH}userprofile.svg`}
                        alt="Mobile"
                      />
                    </div>
                    <p className="gx-p12-sb">
                      {t("mobile_number")}
                      <span className="gx-p14-sb">
                        {company?.mobileNo
                          ? `${company?.mobileCode ?? ""} ${
                              company?.maskMobileNo || company?.mobileNo
                            }`
                          : "N/A"}
                      </span>
                    </p>
                  </div>
                </div>

                {loginName == "admin" ? (
                  <>
                    <div className="information-list">
                      <div className="information-list-child">
                        <div className="information-list-img">
                          <img
                            src={`${ICON_BASE_PATH}onboarded.svg`}
                            alt="Address"
                          />
                        </div>
                        <p className="gx-p12-sb">
                          {t("address")}
                          <span className="gx-p14-sb">
                            {[
                              company?.addressLine1,
                              company?.addressLine2,
                              company?.addressLine3,
                              company?.addressLine4,
                              company?.countryName,
                            ]
                              .filter(Boolean)
                              .join(", ") || "N/A"}
                          </span>
                        </p>
                      </div>
                      <div className="information-status-child">
                        <button
                          onClick={() => handleUpdateClick("address")}
                          className="gx-p12-sb"
                        >
                          {t("update")}
                        </button>
                      </div>
                    </div>
                    <div className="information-list">
                      <div className="information-list-child">
                        <div className="information-list-img">
                          <img
                            src={`${ICON_BASE_PATH}onboarded.svg`}
                            alt="Trade License"
                          />
                        </div>
                        <p className="gx-p12-sb">
                          {t("trade_license_end_date")}
                          <span className="gx-p14-sb">
                            {company?.tradeLicenseExpiryDate ? (
                              <>
                                {formatDate(company?.tradeLicenseExpiryDate)}{" "}
                                {expirationMessage && (
                                  <span
                                    className="gx-p14-sb"
                                    style={{ color: "red" }}
                                  >
                                    ({expirationMessage})
                                  </span>
                                )}
                              </>
                            ) : (
                              "N/A"
                            )}
                          </span>
                        </p>
                      </div>
                      <div className="information-status-child">
                        <button
                          onClick={() => handleUpdateClick("renewnow")}
                          className="gx-p12-sb"
                        >
                          {t("renew_now")}
                        </button>
                      </div>
                    </div>
                    <div className="information-list">
                      <div className="information-list-child">
                        <div className="information-list-img">
                          <img
                            src={`${ICON_BASE_PATH}onboarded.svg`}
                            alt="Company Landline"
                          />
                        </div>
                        <p className="gx-p12-sb">
                          {t("company_landline")}
                          <span className="gx-p14-sb">
                            {company?.companyLandline || "N/A"}
                          </span>
                        </p>
                      </div>
                      <div className="information-status-child">
                        <button
                          onClick={() => handleUpdateClick("landline")}
                          className="gx-p12-sb"
                        >
                          {t("update")}
                        </button>
                      </div>
                    </div>
                    <div className="information-list">
                      <div className="information-list-child">
                        <div className="information-list-img">
                          <img
                            src={`${ICON_BASE_PATH}onboarded.svg`}
                            alt="Company PO Box"
                          />
                        </div>
                        <p className="gx-p12-sb">
                          {t("company_po_box")}
                          <span className="gx-p14-sb">
                            {company?.companyPoBox || "N/A"}
                          </span>
                        </p>
                      </div>
                      <div className="information-status-child">
                        <button
                          onClick={() => handleUpdateClick("poboxInfo")}
                          className="gx-p12-sb"
                        >
                          {t("update")}
                        </button>
                      </div>
                    </div>
                    <div className="information-list">
                      <div className="information-list-child">
                        <div className="information-list-img">
                          <img
                            src={`${ICON_BASE_PATH}onboarded.svg`}
                            alt="GrowX Company ID"
                          />
                        </div>
                        <p className="gx-p12-sb">
                          {t("growx_company_id")}{" "}
                          <span className="gx-p14-sb">
                            {company?.portalCompanyId}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="information-list">
                      <div className="information-list-child">
                        <div className="information-list-img">
                          <img
                            src={`${ICON_BASE_PATH}onboarded.svg`}
                            alt="Total Users Onboarded"
                          />
                        </div>
                        <p className="gx-p12-sb">
                          {t("total_user_onboard")}
                          <span className="gx-p14-sb">
                            {company?.compTotalUsersOnboarded}
                          </span>
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="information-list">
                      <div className="information-list-child">
                        <div className="information-list-img">
                          <img
                            src={`${ICON_BASE_PATH}onboarded.svg`}
                            alt="GrowX Company ID"
                          />
                        </div>
                        <p className="gx-p12-sb">
                          {t("growx_company_id")}{" "}
                          <span className="gx-p14-sb">
                            {company?.portalCompanyId}
                          </span>
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="gx-profile-tab-main">
                <div className="gx-profile-tab-container">
                  <div className="gx-profile-tab-heading">
                    <h3 className="">Receive important updates on Whatsapp</h3>
                    <Switch
                      checked={whatsappOptIn}
                      onChange={handleToggleWhatsapp}
                    />
                  </div>

                  {whatsappOptIn && (
                    <div className="mt-4">
                      <Mobilefiled
                        label={t("whatsapp_number")}
                        value={whatsappNumber}
                        onChange={(val) => setWhatsappNumber(val)}
                        onValidate={(_field, isValid) =>
                          setWhatsappNumberValid(isValid)
                        }
                        countryDialCode={whatsappDialCode}
                        onCountryCodeChange={(dialCode) =>
                          setWhatsappDialCode(dialCode)
                        }
                        triggerValidation={whatsappTriggerValidation}
                        starlable
                        disable={false}
                      />
                    </div>
                  )}
                </div>

                <ProfileStatements
                  vcifId={company?.vcifId}
                  docTypes={documentTypes}
                />
              </div>
            </>
          ) : (
            <NoDataFound className="gx-no-data" message={t("no_data_found")} />
          )}

          <AppDrawer
            title={title}
            subtitle={subtitle}
            openDrawer={isDrawerOpen}
            onClose={handleDrawerClose}
            submit={t("submit")}
            cancel={t("cancel")}
            onSubmit={handleSubmit(onSubmit)}
          >
            <UpdateCompanyInfo
              company={company}
              watch={watch}
              setValue={setValue}
              updateSection={updateSection}
            />
          </AppDrawer>

          <ErrorModal open={isErrorOpen} close={() => setErrorOpen(false)} />
        </>
      )}
    </>
  );
};

export default CompanyInfo;
